import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MapPin, Calendar, ArrowUpRight, Clock, CheckCircle2 } from "lucide-react";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";

const API = process.env.REACT_APP_BACKEND_URL;

function ProjectCard({ project }) {
  const navigate = useNavigate();
  const isOngoing = project.status === "ongoing";

  return (
    <motion.div
      initial={{ opacity: 0, y: 25 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="project-card group cursor-pointer"
      data-testid={`project-card-${project.id}`}
      onClick={() => navigate(`/projeler/${project.id}`)}
    >
      <div className="overflow-hidden relative rounded-t-xl">
        <img src={project.image} alt={project.title} loading="lazy" />
        <div className={`absolute top-0 left-0 px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 ${isOngoing ? "bg-blue-600 text-white" : "bg-emerald-600 text-white"}`} style={{ borderBottomRightRadius: "12px" }} data-testid={`project-status-badge-${project.id}`}>
          {isOngoing ? <Clock size={12} /> : <CheckCircle2 size={12} />}
          {isOngoing ? "Devam Ediyor" : "Tamamlandı"}
        </div>
        {isOngoing && project.progress > 0 && (
          <div className="absolute top-0 right-0 bg-[#FF5A00] text-white text-[11px] font-bold px-3 py-2 rounded-bl-xl">%{project.progress}</div>
        )}
      </div>
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-base sm:text-lg font-bold text-[#0C0C0C] tracking-tight" style={{ fontFamily: "Outfit" }}>{project.title}</h3>
          <ArrowUpRight size={18} className="text-gray-300 group-hover:text-[#FF5A00] transition-colors flex-shrink-0 mt-0.5" />
        </div>
        <p className="text-sm text-gray-500 mb-3 line-clamp-2">{project.description}</p>
        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400">
          <span className="flex items-center gap-1.5"><MapPin size={12} />{project.location}</span>
          <span className="flex items-center gap-1.5"><Calendar size={12} />{project.date}</span>
        </div>
        {project.area && <p className="text-xs text-gray-400 mt-2">Alan: {project.area}</p>}
        {isOngoing && project.progress > 0 && (
          <div className="mt-3 bg-gray-100 rounded-full h-1.5 overflow-hidden">
            <motion.div initial={{ width: 0 }} whileInView={{ width: `${project.progress}%` }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.3 }} className="h-full bg-gradient-to-r from-[#FF5A00] to-[#FF8A3D] rounded-full" />
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default function ProjelerPage() {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") || "ongoing";
  const [ongoing, setOngoing] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [activeTab, setActiveTab] = useState(tabParam);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/api/projects`).then((r) => {
      setOngoing(r.data.filter((p) => p.status === "ongoing"));
      setCompleted(r.data.filter((p) => p.status === "completed"));
    }).catch(() => {});
  }, []);

  useEffect(() => { setActiveTab(tabParam); }, [tabParam]);

  return (
    <div className="min-h-screen bg-white" data-testid="projeler-page">
      <Navbar />
      <div className="pt-20 bg-gradient-to-b from-[#0C0C0C] to-[#1a1a2e] text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-14 sm:py-20 lg:py-28">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="overline justify-start mb-4 sm:mb-5 text-white/60">Projelerimiz</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter" style={{ fontFamily: "Outfit" }}>
            Her projede <span className="text-[#FF5A00]">kalite</span> ve yenilik
          </motion.h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-10 sm:py-16">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-gray-100 rounded-full p-1 sm:p-1.5 mb-8 sm:mb-10 inline-flex" data-testid="projects-tabs">
            <TabsTrigger value="ongoing" data-testid="tab-ongoing" className="rounded-full px-4 sm:px-6 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold data-[state=active]:bg-[#FF5A00] data-[state=active]:text-white data-[state=active]:shadow-md transition-all" style={{ fontFamily: "Outfit" }}>
              Devam Eden ({ongoing.length})
            </TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed" className="rounded-full px-4 sm:px-6 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold data-[state=active]:bg-[#FF5A00] data-[state=active]:text-white data-[state=active]:shadow-md transition-all" style={{ fontFamily: "Outfit" }}>
              Tamamlanan ({completed.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="ongoing">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {ongoing.map((p) => <ProjectCard key={p.id} project={p} />)}
            </div>
            {ongoing.length === 0 && <p className="text-gray-400 text-center py-12">Henüz devam eden proje yok</p>}
          </TabsContent>
          <TabsContent value="completed">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {completed.map((p) => <ProjectCard key={p.id} project={p} />)}
            </div>
            {completed.length === 0 && <p className="text-gray-400 text-center py-12">Henüz tamamlanan proje yok</p>}
          </TabsContent>
        </Tabs>
      </div>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
